import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { EntityDocument } from '../documents/DocumentList'
import { DocumentUpload } from '../documents'
import {
 Card,
 CardContent,
 CardHeader,
 CardTitle,
 ModernButton,
 Label,
 Input,
 Select,
 SelectContent,
 SelectItem,
 SelectTrigger,
 SelectValue,
} from '../ui'
import type { SaleOnboardingContractRow } from '../../types'
import { apiService } from '../../services/api'

type SalePostSalesContractsTabProps = {
 saleId: string
 contracts: SaleOnboardingContractRow[]
 saleDocuments: EntityDocument[]
 canEdit: boolean
 onUpdated: () => void | Promise<void>
 onToast?: (message: string, type: 'success' | 'error') => void
}

export const SalePostSalesContractsTab: React.FC<SalePostSalesContractsTabProps> = ({
 saleId,
 contracts,
 saleDocuments,
 canEdit,
 onUpdated,
 onToast,
}) => {
 const { t } = useTranslation()
 const [contractDateByFulfillment, setContractDateByFulfillment] = useState<Record<string, string>>({})
 const [contractDocByFulfillment, setContractDocByFulfillment] = useState<Record<string, string>>({})
 const [signingId, setSigningId] = useState<string | null>(null)

 const signContract = async (fulfillmentId: string) => {
 const dateVal = contractDateByFulfillment[fulfillmentId]
 if (!dateVal) {
 onToast?.(t('financialSales.saleOnboarding.pickSignDate'), 'error')
 return
 }
 const signedAt = new Date(dateVal).toISOString()
 const rawDoc = contractDocByFulfillment[fulfillmentId]
 const entityDocumentId = rawDoc && rawDoc.length > 0 ? rawDoc : undefined
 try {
 setSigningId(fulfillmentId)
 await apiService.patchSaleContractFulfillment(saleId, fulfillmentId, {
 signedAt,
 ...(entityDocumentId ? { entityDocumentId } : {}),
 })
 onToast?.(t('financialSales.saleOnboarding.contractSigned'), 'success')
 await onUpdated()
 } catch {
 onToast?.(t('financialSales.saleOnboarding.contractSignError'), 'error')
 } finally {
 setSigningId(null)
 }
 }

 if (contracts.length === 0) {
 return (
 <p className="text-sm text-muted-foreground">{t('financialSales.saleDocumentation.noContracts')}</p>
 )
 }

 return (
 <div className="space-y-6">
 <Card className="border-border">
 <CardHeader className="pb-3">
 <CardTitle className="text-base">{t('financialSales.saleDocumentation.uploadSaleContractTitle')}</CardTitle>
 </CardHeader>
 <CardContent>
 {canEdit ? (
 <DocumentUpload
 entityType="sale"
 entityId={saleId}
 saleId={saleId}
 documentType="signed_contract"
 label={t('financialSales.saleDocumentation.uploadSignedContract')}
 onUploadSuccess={() => void onUpdated()}
 onUploadError={(msg) => onToast?.(msg, 'error')}
 />
 ) : (
 <p className="text-xs text-muted-foreground">{t('financialSales.saleDocumentation.readOnlyHint')}</p>
 )}
 </CardContent>
 </Card>

 <div className="space-y-4">
 <h3 className="text-sm font-semibold">{t('financialSales.saleOnboarding.contractsSection')}</h3>
 {contracts.map((row) => (
 <div key={row.fulfillmentId} className="rounded-lg border border-border p-4 space-y-3">
 <div className="flex flex-wrap items-center justify-between gap-2">
 <p className="text-sm font-medium">{row.name}</p>
 {row.signedAt ? (
 <span className="text-xs rounded-full bg-accent/15 text-accent-foreground px-2 py-0.5">
 {t('financialSales.saleDocumentation.signedBadge')}
 </span>
 ) : (
 <span className="text-xs rounded-full bg-muted px-2 py-0.5 text-muted-foreground">
 {t('financialSales.saleDocumentation.pendingBadge')}
 </span>
 )}
 </div>
 {row.referenceFileUrl && (
 <a
 href={row.referenceFileUrl}
 target="_blank"
 rel="noopener noreferrer"
 className="text-xs text-accent hover:underline inline-block"
 >
 {t('financialSales.saleOnboarding.openTemplate')}
 </a>
 )}
 <div className="grid gap-3 sm:grid-cols-2">
 <div>
 <Label className="text-xs">{t('financialSales.saleOnboarding.signDate')}</Label>
 <Input
 type="date"
 className="mt-1"
 value={contractDateByFulfillment[row.fulfillmentId] || ''}
 onChange={(e) =>
 setContractDateByFulfillment((prev) => ({
 ...prev,
 [row.fulfillmentId]: e.target.value,
 }))
 }
 disabled={!canEdit}
 />
 </div>
 <div>
 <Label className="text-xs">{t('financialSales.saleOnboarding.signedPdfOptional')}</Label>
 <Select
 value={contractDocByFulfillment[row.fulfillmentId] || '__none__'}
 onValueChange={(v) =>
 setContractDocByFulfillment((prev) => ({
 ...prev,
 [row.fulfillmentId]: v === '__none__' ? '' : v,
 }))
 }
 disabled={!canEdit}
 >
 <SelectTrigger className="mt-1">
 <SelectValue placeholder={t('financialSales.saleOnboarding.optional')} />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="__none__">{t('financialSales.saleOnboarding.none')}</SelectItem>
 {saleDocuments.map((d) => (
 <SelectItem key={d.id} value={d.id}>
 {d.name}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 </div>
 <ModernButton
 type="button"
 size="sm"
 disabled={!canEdit || signingId === row.fulfillmentId}
 onClick={() => void signContract(row.fulfillmentId)}
 >
 {signingId === row.fulfillmentId
 ? t('financialSales.saleOnboarding.saving')
 : row.signedAt
 ? t('financialSales.saleDocumentation.updateSignature')
 : t('financialSales.saleOnboarding.confirmSignature')}
 </ModernButton>
 </div>
 ))}
 </div>
 </div>
 )
}
